<template>
  <div class="panel">
    <!--
      Fieldset-legend visual: the title sits on top of the panel's
      yellow border line at left:30px so the line appears to break
      around the title. The title's solid background "punches" the
      gap so the yellow rule visually stops at the glyph edges.
    -->
    <span v-if="title" class="panel-title">{{ title }}</span>
    <div class="panel-body">
      <slot />
    </div>
  </div>
</template>

<script>
// Generic deployatron panel chrome — yellow border, Starjedi-titled
// knockout legend at top-left. Rendered around any list-style block
// that wants the host's signature treatment (queue panel, projects
// panel, etc.). Content goes in the default slot.
//
// Visual contract is intentionally narrow: the chrome alone, no
// inner padding rules that fight a row's hover-band that wants to
// stretch edge-to-edge. Consumers control their own internal layout
// (padding on header rows, hover bands stretched to the panel
// border, etc.).
//
// The corresponding chrome on the queue panel
// (@bloglovin/queue/src/Components/DeveloperQueuePanel.vue) is the
// reference implementation — that file still owns its own copy for
// now since cross-app component sharing is not wired. Refactoring
// the queue panel onto this component is a follow-up.
export default {
  name: "Panel",
  props: {
    title: {
      type: String,
      required: false,
      default: "",
    },
  },
};
</script>

<style scoped>
.panel {
  position: relative;
  border: 2px solid yellow;
  border-radius: 4px;
  /* Horizontal padding is 0 so a row inside the panel can stretch
     its hover background edge-to-edge to the yellow border. Top
     padding gives the title knockout breathing room before the
     first content row. Vertical balance below mirrors the queue
     panel's geometry so multiple panels stacked together visually
     match. */
  padding: 26px 0 16px 0;
  /* The title is positioned with translateY(-50%) so half of it
     bleeds above the border line. Reserve 30 px above each panel so
     overflow doesn't clip when the panel sits flush at the top of
     a viewport / column. */
  margin-top: 30px;
  /* Floor to keep horizontal divider rules (and other content that
     wants room to stretch) readable on narrow viewports — without
     a floor the parent column collapses to the longest single line.
     Cap so the panel doesn't fill the whole column on a wide
     viewport. */
  min-width: 400px;
  max-width: 480px;
  box-sizing: border-box;
}

.panel-title {
  position: absolute;
  /* Sits 6 px above the panel's top border line, 30 px in from the
     left. translateY(-50%) keeps the title's mid-line on its
     ride-height, just shifted upward by the explicit `top` offset. */
  top: -6px;
  left: 30px;
  transform: translateY(-50%);
  /* Background must match the page background behind the panel so
     the border line "stops" where the title is. base.scss sets the
     page to black; if the page background changes, update this
     color too (or wire it through a CSS custom property). */
  background: black;
  color: yellow;
  /* Horizontal padding widens the knockout window a little past the
     glyph edges so the title visibly breaks the line on both sides. */
  padding: 0 12px;
  /* Star Wars-styled face declared in base.scss (registered via
     @font-face from the cdn-alpha host). The fallback chain keeps
     the title legible if the network font fails to load. */
  font-family: 'Starjedi', 'TradeGothic', sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 1;
  white-space: nowrap;
}

.panel-body {
  display: flex;
  flex-direction: column;
}
</style>
